import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Stack, Alert, CircularProgress,
  IconButton, Tooltip, Divider, Radio, Avatar, MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import api from '../services/api';

interface ResolvedItem {
  product_name: string | null;
  sku: string | null;
  item_spec: string | null;
  image_src: string | null;
}

interface Resolved {
  order_sn: string;
  found: boolean;
  shipped_by_user_id: number | null;
  shipped_by_name: string | null;
  shipped_at: string | null;
  attribution: 'shipped' | 'last_receiver' | null;
  items: ResolvedItem[];
}

interface Employee { id: number; name: string; }

interface MisShipment {
  id: number;
  order_sn: string;
  shipped_by_user_id: number | null;
  shipped_by_name: string | null;
  shipped_at: string | null;
  product_name: string | null;
  sku: string | null;
  item_spec: string | null;
  product_image_url: string | null;
  customer_image_url: string | null;
  wrong_description: string;
  correct_item: string | null;
  notes: string | null;
  created_by_name: string | null;
  created_at: string;
}

const fmt = (ts: string | null) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString('zh-TW', { hour12: false });
};

const MisShipmentPage: React.FC = () => {
  const [records, setRecords] = useState<MisShipment[]>([]);
  const [loading, setLoading] = useState(true);

  // form state
  const [orderSn, setOrderSn] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const [resolveErr, setResolveErr] = useState('');
  const [selectedIdx, setSelectedIdx] = useState<number>(-1);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [shipperId, setShipperId] = useState<number | ''>('');
  const [wrongDesc, setWrongDesc] = useState('');
  const [correctItem, setCorrectItem] = useState('');
  const [notes, setNotes] = useState('');
  const [custFile, setCustFile] = useState<File | null>(null);
  const [custPreview, setCustPreview] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/misshipments');
      setRecords(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  useEffect(() => {
    api.get('/api/misshipments/employees')
      .then(res => setEmployees(res.data || []))
      .catch(e => console.error(e));
  }, []);

  const handleResolve = async () => {
    const sn = orderSn.trim();
    if (!sn) return;
    setResolving(true);
    setResolveErr('');
    setResolved(null);
    setSelectedIdx(-1);
    try {
      const res = await api.get(`/api/misshipments/resolve/${encodeURIComponent(sn)}`);
      const data: Resolved = res.data;
      setResolved(data);
      // auto-fill shipper if attributed
      setShipperId(data.shipped_by_user_id ?? '');
      // auto-select the only item
      if (data.items.length === 1) setSelectedIdx(0);
      if (!data.found) {
        setResolveErr('找不到此訂單的出貨紀錄，仍可手動填寫並新增。');
      }
    } catch (e: any) {
      setResolveErr(e?.response?.data?.message || '查詢失敗');
    } finally {
      setResolving(false);
    }
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setCustFile(f);
    if (custPreview) URL.revokeObjectURL(custPreview);
    setCustPreview(f ? URL.createObjectURL(f) : '');
  };

  const resetForm = () => {
    setOrderSn(''); setResolved(null); setResolveErr(''); setSelectedIdx(-1);
    setShipperId('');
    setWrongDesc(''); setCorrectItem(''); setNotes(''); setSaveErr('');
    setCustFile(null);
    if (custPreview) URL.revokeObjectURL(custPreview);
    setCustPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!orderSn.trim()) { setSaveErr('請輸入訂單編號'); return; }
    if (!wrongDesc.trim()) { setSaveErr('請填寫寄錯內容'); return; }
    setSaving(true);
    setSaveErr('');
    try {
      const picked = selectedIdx >= 0 ? resolved?.items?.[selectedIdx] : undefined;
      const shipperName = shipperId !== ''
        ? (employees.find(e => e.id === shipperId)?.name || resolved?.shipped_by_name || null)
        : null;
      const fd = new FormData();
      fd.append('order_sn', orderSn.trim());
      if (shipperId !== '') fd.append('shipped_by_user_id', String(shipperId));
      if (shipperName) fd.append('shipped_by_name', shipperName);
      if (resolved?.shipped_at) fd.append('shipped_at', resolved.shipped_at);
      if (picked?.product_name) fd.append('product_name', picked.product_name);
      if (picked?.sku) fd.append('sku', picked.sku);
      if (picked?.item_spec) fd.append('item_spec', picked.item_spec);
      if (picked?.image_src) fd.append('product_image_url', picked.image_src);
      fd.append('wrong_description', wrongDesc.trim());
      if (correctItem.trim()) fd.append('correct_item', correctItem.trim());
      if (notes.trim()) fd.append('notes', notes.trim());
      if (custFile) fd.append('customer_image', custFile);

      await api.post('/api/misshipments', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      resetForm();
      await loadRecords();
    } catch (e: any) {
      setSaveErr(e?.response?.data?.message || '新增失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('確定刪除這筆紀錄？')) return;
    try {
      await api.delete(`/api/misshipments/${id}`);
      setRecords(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ReportProblemOutlinedIcon color="warning" />
        <Typography variant="h4">寄錯紀錄</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        記錄寄錯的訂單，讓大家看到自己出錯的地方，避免再犯同樣的錯誤。
      </Typography>

      {/* ---- Add record form ---- */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>新增寄錯紀錄</Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <TextField
            label="訂單編號"
            value={orderSn}
            onChange={(e) => setOrderSn(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleResolve(); }}
            placeholder="例如 260520TK4MRG7S"
            size="small"
            fullWidth
          />
          <Button
            variant="outlined"
            startIcon={resolving ? <CircularProgress size={16} /> : <SearchIcon />}
            onClick={handleResolve}
            disabled={resolving || !orderSn.trim()}
            sx={{ whiteSpace: 'nowrap', minWidth: 120 }}
          >
            查詢出貨人
          </Button>
        </Stack>

        {resolveErr && <Alert severity="info" sx={{ mb: 2 }}>{resolveErr}</Alert>}

        {resolved && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: resolved.items.length ? 2 : 0 }} alignItems={{ sm: 'flex-start' }}>
              <Box sx={{ minWidth: 220 }}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="出貨人"
                  value={shipperId}
                  onChange={(e) => setShipperId(e.target.value === '' ? '' : Number(e.target.value))}
                  helperText={
                    resolved.attribution === 'shipped'
                      ? '系統自動帶出（出貨紀錄），可修改'
                      : resolved.attribution === 'last_receiver'
                      ? '查無出貨紀錄，已帶出最後處理此訂單的人（推測），可修改'
                      : '此訂單查無任何處理紀錄，請手動選擇'
                  }
                >
                  <MenuItem value=""><em>未指定</em></MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>{emp.name}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {resolved.attribution === 'last_receiver' ? '最後處理時間' : '出貨時間'}
                </Typography>
                <Typography variant="body2">{fmt(resolved.shipped_at)}</Typography>
              </Box>
            </Stack>

            {resolved.items.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  選擇寄錯的商品（訂單內商品）
                </Typography>
                <Stack spacing={1}>
                  {resolved.items.map((it, i) => (
                    <Paper
                      key={i}
                      variant="outlined"
                      onClick={() => setSelectedIdx(i)}
                      sx={{
                        p: 1, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
                        borderColor: selectedIdx === i ? 'warning.main' : 'divider',
                        borderWidth: selectedIdx === i ? 2 : 1,
                        bgcolor: selectedIdx === i ? 'warning.50' : 'background.paper',
                      }}
                    >
                      <Radio checked={selectedIdx === i} size="small" sx={{ p: 0.5 }} />
                      <Avatar
                        variant="rounded"
                        src={it.image_src || undefined}
                        sx={{ width: 52, height: 52, bgcolor: 'grey.200' }}
                      >
                        {!it.image_src && '?'}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{it.product_name || '(未命名商品)'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {it.item_spec ? `${it.item_spec} · ` : ''}{it.sku ? `SKU ${it.sku}` : ''}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        )}

        {/* ---- comparison: correct product vs customer image ---- */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">應寄商品（系統圖）</Typography>
            <Box
              sx={{
                mt: 0.5, height: 200, border: '1px dashed', borderColor: 'divider', borderRadius: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                bgcolor: 'grey.50',
              }}
            >
              {selectedIdx >= 0 && resolved?.items?.[selectedIdx]?.image_src ? (
                <img
                  src={resolved.items[selectedIdx].image_src!}
                  alt="product"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              ) : (
                <Typography variant="caption" color="text.secondary">選擇上方商品後顯示</Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">客人提供的圖片（實際收到）</Typography>
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                mt: 0.5, height: 200, border: '1px dashed', borderColor: 'divider', borderRadius: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                bgcolor: 'grey.50', cursor: 'pointer', flexDirection: 'column',
              }}
            >
              {custPreview ? (
                <img src={custPreview} alt="customer" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              ) : (
                <>
                  <PhotoCameraOutlinedIcon color="disabled" />
                  <Typography variant="caption" color="text.secondary">點此上傳客人圖片</Typography>
                </>
              )}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={onPickFile}
            />
            {custFile && (
              <Button size="small" sx={{ mt: 0.5 }} onClick={() => { setCustFile(null); if (custPreview) URL.revokeObjectURL(custPreview); setCustPreview(''); if (fileInputRef.current) fileInputRef.current.value=''; }}>
                移除圖片
              </Button>
            )}
          </Box>
        </Stack>

        <TextField
          label="寄錯內容（寄錯了什麼）"
          value={wrongDesc}
          onChange={(e) => setWrongDesc(e.target.value)}
          placeholder="例如：寄成白色 M，客人訂的是黑色 L"
          size="small"
          fullWidth
          required
          multiline
          minRows={2}
          sx={{ mb: 2 }}
        />
        <TextField
          label="正確商品（應該寄的）"
          value={correctItem}
          onChange={(e) => setCorrectItem(e.target.value)}
          placeholder="例如：黑色 L · SKU 1995"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="備註 / 如何避免"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="例如：出貨前核對顏色與尺寸標籤"
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={{ mb: 2 }}
        />

        {saveErr && <Alert severity="error" sx={{ mb: 2 }}>{saveErr}</Alert>}

        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? '新增中…' : '新增紀錄'}
          </Button>
          <Button variant="text" onClick={resetForm} disabled={saving}>清除</Button>
        </Stack>
      </Paper>

      <Divider sx={{ mb: 3 }} />

      {/* ---- Records table ---- */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        全部紀錄 {records.length > 0 && `(${records.length})`}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : records.length === 0 ? (
        <Alert severity="success">目前沒有寄錯紀錄 🎉</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>登記時間</TableCell>
                <TableCell>訂單編號</TableCell>
                <TableCell>出貨人</TableCell>
                <TableCell>應寄商品</TableCell>
                <TableCell>客人圖片</TableCell>
                <TableCell>寄錯內容</TableCell>
                <TableCell>正確商品</TableCell>
                <TableCell>備註</TableCell>
                <TableCell>登記人</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(r.created_at)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.order_sn}</TableCell>
                  <TableCell>
                    {r.shipped_by_name
                      ? <Chip label={r.shipped_by_name} size="small" color="warning" variant="outlined" />
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {r.product_image_url ? (
                      <a href={r.product_image_url} target="_blank" rel="noreferrer">
                        <Avatar variant="rounded" src={r.product_image_url} sx={{ width: 44, height: 44 }} />
                      </a>
                    ) : '—'}
                    {r.product_name && (
                      <Typography variant="caption" display="block" sx={{ maxWidth: 120 }} noWrap>
                        {r.product_name}{r.item_spec ? ` (${r.item_spec})` : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.customer_image_url ? (
                      <a href={r.customer_image_url} target="_blank" rel="noreferrer">
                        <Avatar variant="rounded" src={r.customer_image_url} sx={{ width: 44, height: 44 }} />
                      </a>
                    ) : '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: 'pre-wrap' }}>{r.wrong_description}</TableCell>
                  <TableCell sx={{ maxWidth: 160, whiteSpace: 'pre-wrap' }}>{r.correct_item || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 180, whiteSpace: 'pre-wrap' }}>{r.notes || '—'}</TableCell>
                  <TableCell>{r.created_by_name || '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="刪除">
                      <IconButton size="small" onClick={() => handleDelete(r.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default MisShipmentPage;
